import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Header from '@/pages/landing/components/Header';
import Footer from '@/pages/landing/components/Footer';
import { forkOutcome } from '@/api';
import { getUser, isLoggedIn } from '@/auth/token';
import { outcomePath, type PublicOutcomeSummary } from '@/pages/landing/outcomeShowcase';
import { useCertifiedPlaybook } from '@/pages/playbooks/usePlaybook';
import {
  categoryIcon,
  formatPrice,
  playbookCapabilities,
  playbookFaqs,
  playbookTestResults,
  toMarketplaceCard,
} from './mapPlaybook';

const STATUS_STYLES = {
  passed: { label: 'Passed', dot: 'bg-success', text: 'text-success' },
  failed: { label: 'Failed', dot: 'bg-error', text: 'text-error' },
} as const;

export default function PlaybookDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [outcome, setOutcome] = useState<PublicOutcomeSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [getState, setGetState] = useState<'idle' | 'added' | 'paywall' | 'busy'>('idle');
  const [forkErr, setForkErr] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const logged = isLoggedIn();
  const isAdmin = getUser()?.role === 'admin';

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setOutcome(null);
    setError(null);
    setGetState('idle');
    fetch(`/api/outcomes/public/${encodeURIComponent(id)}`)
      .then((res) => {
        if (res.status === 404) throw new Error('not-found');
        if (!res.ok) throw new Error('failed');
        return res.json();
      })
      .then((data) => {
        if (!cancelled) setOutcome(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error && err.message === 'not-found' ? 'not-found' : 'failed');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const playbook = useMemo(() => (outcome ? toMarketplaceCard(outcome) : null), [outcome]);

  useEffect(() => {
    document.title = playbook ? `${playbook.name} · Sourcetape Marketplace` : 'Playbook · Sourcetape';
    window.scrollTo(0, 0);
  }, [playbook]);

  const chrome = { logged, isAdmin };

  if (error === 'not-found' || (error === null && outcome === null && !id)) {
    return (
      <div className="min-h-screen bg-bg text-textp">
        <Header {...chrome} />
        <main className="max-w-[1040px] mx-auto px-6 py-24 text-center">
          <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-xl bg-accentSoft border border-accentLine">
            <i className="ri-emotion-sad-line text-accent text-2xl" />
          </span>
          <h1 className="mt-4 text-2xl font-semibold">Playbook not found</h1>
          <p className="mt-2 text-texts">This playbook does not exist or is not published.</p>
          <Link
            to="/marketplace"
            className="whitespace-nowrap mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-accent-text bg-accent hover:bg-accent-hover px-5 py-2.5 rounded-lg transition-colors"
          >
            <i className="ri-arrow-left-line" />
            Back to marketplace
          </Link>
        </main>
        <Footer logged={logged} />
      </div>
    );
  }

  if (error === 'failed') {
    return (
      <div className="min-h-screen bg-bg text-textp">
        <Header {...chrome} />
        <main className="max-w-[1040px] mx-auto px-6 py-24 text-center">
          <h1 className="text-2xl font-semibold">Could not load this playbook</h1>
          <p className="mt-2 text-texts">The registry did not respond.</p>
        </main>
        <Footer logged={logged} />
      </div>
    );
  }

  if (!outcome || !playbook) {
    return (
      <div className="min-h-screen bg-bg text-textp">
        <Header {...chrome} />
        <main className="max-w-[1040px] mx-auto px-6 py-24 text-center text-sm text-texts">Loading playbook…</main>
        <Footer logged={logged} />
      </div>
    );
  }

  const icon = categoryIcon(playbook.category);
  const free = playbook.priceCents === 0;
  const testResults = playbookTestResults(outcome);
  const capabilities = playbookCapabilities(outcome);
  const faqs = playbookFaqs(outcome);
  const proofPath = outcomePath(outcome);

  const handleGet = async () => {
    setForkErr(null);
    if (!free) {
      setGetState('paywall');
      return;
    }
    setGetState('busy');
    const result = await useCertifiedPlaybook({
      outcomeId: outcome.id,
      loggedIn: logged,
      navigate,
      forkApi: forkOutcome,
    });
    if (!result.ok) {
      if (result.error) setForkErr(result.error);
      setGetState('idle');
      return;
    }
    setGetState('added');
  };

  return (
    <div className="min-h-screen bg-bg text-textp" data-testid="marketplace-detail">
      <Header {...chrome} />

      <div className="sticky top-16 z-40 bg-bg/95 backdrop-blur border-b border-border">
        <div className="max-w-[1040px] mx-auto px-6 flex items-center gap-1 overflow-x-auto">
          <Link
            to="/marketplace"
            className="whitespace-nowrap shrink-0 flex items-center gap-1 text-sm text-texts hover:text-textp px-2 py-3 transition-colors"
          >
            <i className="ri-arrow-left-line" />
            Marketplace
          </Link>
          <span className="text-texts/50 shrink-0">/</span>
          <span className="whitespace-nowrap text-sm text-texts px-2 py-3 truncate max-w-[200px]">{playbook.name}</span>
          <div className="ml-auto flex items-center gap-1 shrink-0">
            {[
              { href: '#overview', label: 'Overview' },
              { href: '#capabilities', label: 'Capabilities' },
              { href: '#tests', label: 'Tests' },
              { href: '#faq', label: 'FAQ' },
            ].map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="whitespace-nowrap text-sm text-texts hover:text-textp px-3 py-3 transition-colors"
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
      </div>

      <main>
        <section id="overview" className="scroll-mt-36 border-b border-border">
          <div className="max-w-[1040px] mx-auto px-6 py-12 md:py-16">
            <div className="flex flex-col lg:flex-row lg:items-start gap-8 lg:gap-12">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-accent-text bg-accent px-3 py-1 rounded-full">
                    {playbook.category}
                  </span>
                </div>

                <h1 className="mt-4 font-heading text-3xl md:text-5xl tracking-tight font-semibold text-textp">
                  {playbook.name}
                </h1>
                <p className="mt-2 text-lg text-texts">{playbook.tagline}</p>
                <p className="mt-5 text-texts leading-relaxed max-w-[640px]">{playbook.description}</p>

                <div className="mt-6 flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2.5">
                    <span className="w-9 h-9 shrink-0 rounded-full bg-surface2 border border-border flex items-center justify-center text-sm font-medium text-texts">
                      {playbook.authorInitials}
                    </span>
                    <div>
                      <div className="text-sm font-medium text-textp">{playbook.author}</div>
                      <div className="text-xs text-texts">Creator</div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex items-center gap-2 flex-wrap">
                  {playbook.tags.map((t) => (
                    <span
                      key={t}
                      className="whitespace-nowrap text-xs text-texts bg-surface2 border border-border px-2.5 py-1 rounded-full"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>

              <div className="w-full lg:w-[300px] shrink-0">
                <div className="bg-surface border border-border rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-texts">SkillsBench score</span>
                    <span className="text-sm font-mono text-success">{playbook.score.toFixed(1)}/12</span>
                  </div>
                  <div className="mt-5 grid grid-cols-3 gap-3 text-center">
                    <div>
                      <div className="text-base font-mono text-textp">{playbook.steps}</div>
                      <div className="text-xs text-texts">steps</div>
                    </div>
                    <div>
                      <div className="text-base font-mono text-textp">{playbook.downloads.toLocaleString()}</div>
                      <div className="text-xs text-texts">downloads</div>
                    </div>
                    <div>
                      <div className="text-base font-mono text-textp">{playbook.score.toFixed(1)}</div>
                      <div className="text-xs text-texts">score</div>
                    </div>
                  </div>

                  <div className="mt-6 pt-5 border-t border-border">
                    <div className="text-2xl font-semibold text-textp">{formatPrice(playbook.priceCents)}</div>
                    {forkErr && <p className="mt-2 text-xs text-error">{forkErr}</p>}
                    <button
                      type="button"
                      data-testid="marketplace-get"
                      onClick={handleGet}
                      disabled={getState === 'busy' || getState === 'added'}
                      className="whitespace-nowrap mt-3 w-full flex items-center justify-center gap-2 text-sm font-medium text-accent-text bg-accent hover:bg-accent-hover disabled:opacity-60 px-4 py-2.5 rounded-lg transition-colors"
                    >
                      {getState === 'idle' && (
                        <>
                          <i className="ri-download-2-line" />
                          {free ? 'Add to library' : `Get for ${formatPrice(playbook.priceCents)}`}
                        </>
                      )}
                      {getState === 'busy' && 'Opening…'}
                      {getState === 'added' && (
                        <>
                          <i className="ri-check-line" />
                          Added to your library
                        </>
                      )}
                      {getState === 'paywall' && (
                        <>
                          <i className="ri-lock-line" />
                          Checkout is wired up next
                        </>
                      )}
                    </button>
                    {getState === 'paywall' && (
                      <p className="mt-3 text-xs text-texts text-center">
                        Payments connect through Stripe. Ready to hook up once playbook billing is enabled.
                      </p>
                    )}
                    <Link to={proofPath} className="mt-3 block text-center text-xs text-texts hover:text-textp">
                      View certified proof page
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="capabilities" className="scroll-mt-36 py-14 md:py-16 border-b border-border">
          <div className="max-w-[1040px] mx-auto px-6">
            <div className="flex items-center gap-2.5">
              <span className="w-10 h-10 shrink-0 rounded-lg bg-accentSoft border border-accentLine flex items-center justify-center">
                <i className={`${icon} text-accent text-lg`} />
              </span>
              <h2 className="font-heading text-2xl md:text-3xl tracking-tight font-semibold text-textp">Capabilities</h2>
            </div>
            <p className="mt-2 text-texts max-w-[560px]">What this playbook can do, from its contract and composition.</p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {capabilities.map((c) => (
                <div key={c.title + c.description} className="bg-surface border border-border rounded-xl p-5 hover:border-accentLine transition-colors">
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accentSoft border border-accentLine">
                    <i className={`${c.icon} text-accent text-lg`} />
                  </span>
                  <h3 className="mt-4 text-base font-semibold text-textp">{c.title}</h3>
                  <p className="mt-1.5 text-sm text-texts leading-relaxed">{c.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="tests" className="scroll-mt-36 py-14 md:py-16 border-b border-border">
          <div className="max-w-[1040px] mx-auto px-6">
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight font-semibold text-textp">Test results</h2>
            <p className="mt-2 text-texts max-w-[560px]">
              Rubric checks from the published score. We do not invent a pass rate when the record has none.
            </p>

            {!testResults ? (
              <div className="mt-8 bg-surface border border-border rounded-xl p-8 text-sm text-texts">
                No validation suite is published on this playbook yet. Open the{' '}
                <Link to={proofPath} className="text-accent hover:underline">
                  certified proof page
                </Link>{' '}
                if one exists.
              </div>
            ) : (
              <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-1 bg-surface border border-border rounded-xl p-6">
                  <div className="text-xs text-texts">Overall pass rate</div>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="text-4xl font-mono text-success">{testResults.passRate}%</span>
                    <span className="text-sm text-texts">pass rate</span>
                  </div>
                  <div className="mt-4 h-2 rounded-full bg-surface2 overflow-hidden">
                    <div className="h-full bg-success rounded-full" style={{ width: `${testResults.passRate}%` }} />
                  </div>
                  <div className="mt-6 space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-texts">
                        <span className="w-2 h-2 rounded-full bg-success" />
                        Passed
                      </span>
                      <span className="font-mono text-textp">{testResults.passed}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-texts">
                        <span className="w-2 h-2 rounded-full bg-error" />
                        Failed
                      </span>
                      <span className="font-mono text-textp">{testResults.failed}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm pt-3 border-t border-border">
                      <span className="text-texts">Total checks</span>
                      <span className="font-mono text-textp">{testResults.total}</span>
                    </div>
                    {testResults.runtime ? (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-texts">Runtime</span>
                        <span className="font-mono text-textp">{testResults.runtime}</span>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="lg:col-span-2 bg-surface border border-border rounded-xl p-6">
                  <div className="text-xs text-texts">Validation summary</div>
                  <p className="mt-2 text-sm text-textp leading-relaxed">{testResults.summary}</p>
                  {testResults.suites.length > 0 && (
                    <div className="mt-6 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border text-left text-xs text-texts">
                            <th className="py-2 pr-4 font-medium">Check</th>
                            <th className="py-2 pr-4 font-medium">Status</th>
                            <th className="py-2 font-medium text-right">Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          {testResults.suites.map((s) => {
                            const st = STATUS_STYLES[s.status];
                            return (
                              <tr key={s.name} className="border-b border-border/60 last:border-0">
                                <td className="py-3 pr-4 text-textp">{s.name}</td>
                                <td className="py-3 pr-4 whitespace-nowrap">
                                  <span className={`inline-flex items-center gap-1.5 ${st.text}`}>
                                    <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                                    {st.label}
                                  </span>
                                </td>
                                <td className="py-3 text-right font-mono text-texts">{s.score.toFixed(1)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>

        <section id="faq" className="scroll-mt-36 py-14 md:py-16">
          <div className="max-w-[1040px] mx-auto px-6">
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight font-semibold text-textp">
              Frequently asked questions
            </h2>
            <p className="mt-2 text-texts">Everything you need to know before you add this playbook.</p>

            <div className="mt-8 max-w-[720px] divide-y divide-border border-y border-border">
              {faqs.map((f, i) => {
                const open = openFaq === i;
                return (
                      <div key={f.question + String(i)}>
                    <button
                      type="button"
                      onClick={() => setOpenFaq(open ? null : i)}
                      className="w-full flex items-center justify-between gap-4 py-4 text-left"
                    >
                      <span className="text-sm font-medium text-textp">{f.question}</span>
                      <span className="shrink-0 w-6 h-6 flex items-center justify-center rounded-md text-texts">
                        <i className={`ri-arrow-down-s-line text-lg transition-transform ${open ? 'rotate-180' : ''}`} />
                      </span>
                    </button>
                    {open && <p className="pb-4 text-sm text-texts leading-relaxed">{f.answer}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      <Footer logged={logged} />
    </div>
  );
}
